import { defineStore } from 'pinia'

import {
    generateFacebookAuthLink,
    checkFacebookCompleteAuth,
    logoutFacebookAccount,
    getFacebookAdsAccounts,
    updateFacebookAdsAccount,
    getFacebookPixels,
    updatePixel,
    getParametersDataSources,
    updateParametersDataSources,
} from '@/api/call-tracking/traffic-sources/facebook-integration-routes'

import { getRedirectRoute } from '@/helpers/integration-hepler'
import { prepareCustomerParameters, parseParams } from '@/helpers/traffic-sources/facebook-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailTrafficSourceStore } from '@/store/call-tracking/trafficSources/detailTrafficSourceStore'

import { FACEBOOK_AUTH_STATUSES } from '@/constants/call-tracking/trafficSources/facebookAuthStatuses'

export const useDetailTrafficSourceFacebookStore = defineStore('detailTrafficSourceFacebookStore', {
    state: () => ({
        facebookLoading: false,
        facebookAdsAccountsLoading: false,
        facebookPixelLoading: false,

        facebookAdsAccounts: [],
        facebookPixels: [],

        customerParameters: [],
        parametersDataSources: [],
    }),

    getters: {
        trafficSourceSlug() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()
            return detailTrafficSourceStore.slug
        },
    },

    actions: {
        setFacebookAdsCustomerParameters(params) {
            this.customerParameters = prepareCustomerParameters(params)
        },

        updateIntegration(integration) {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            detailTrafficSourceStore.updateIntegration(integration)
        },

        changeIntegrationAfterLogout() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            const integration = detailTrafficSourceStore.integration
            const { app_account = {} } = integration

            this.updateIntegration({
                ...integration,
                app_account: {
                    ...app_account,
                    auth_status: FACEBOOK_AUTH_STATUSES.unauthorized,
                },
            })
        },

        setPixels(facebookPixels) {
            this.facebookPixels = facebookPixels
        },

        async goToGenerateFacebookLink() {
            this.facebookLoading = true

            const { success, payload, message } = await generateFacebookAuthLink({
                slug: this.trafficSourceSlug,
                params: {
                    redirect_route: getRedirectRoute(';;'),
                },
            })

            if (!success) {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.facebookLoading = false

            return { payload, success }
        },

        async goToCheckFacebookCompleteAuth(params) {
            const defaultStore = useDefaultStore()

            this.facebookLoading = true

            const { success, payload, message } = await checkFacebookCompleteAuth({
                slug: this.trafficSourceSlug,
                params,
            })

            if (success) {
                const { integration = {} } = payload

                this.updateIntegration(integration)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.facebookLoading = false
        },

        async goToLogoutFacebookAccount() {
            const defaultStore = useDefaultStore()

            this.facebookLoading = true

            const { success, message } = await logoutFacebookAccount(this.trafficSourceSlug)

            if (success) {
                this.changeIntegrationAfterLogout()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.facebookLoading = false
        },

        async goToLoadFacebookAdsAccounts() {
            this.facebookAdsAccountsLoading = true

            const { success, payload, message } = await getFacebookAdsAccounts(this.trafficSourceSlug)

            if (success) {
                this.facebookAdsAccounts = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.facebookAdsAccountsLoading = false
        },

        async goToUpdateFacebookAdsAccount(adsAccountId) {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await updateFacebookAdsAccount({
                slug: this.trafficSourceSlug,
                adsAccountId,
            })

            if (success) {
                this.updateIntegration(payload)
                this.goToLoadFacebookPixels()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToLoadFacebookPixels() {
            this.facebookPixelLoading = true

            const { success, payload, message } = await getFacebookPixels(this.trafficSourceSlug)

            if (success) {
                const { data = [] } = payload

                this.setPixels(data)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.facebookPixelLoading = false
        },

        async goToUpdatePixel(pixelId) {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await updatePixel({
                slug: this.trafficSourceSlug,
                pixelId,
            })

            if (success) {
                this.updateIntegration(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToLoadParametersDataSources() {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await getParametersDataSources()

            if (success) {
                this.parametersDataSources = parseParams(payload.data_sources)
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToUpdateCustomerParameters({ rowIndex, value, isSelected }) {
            const defaultStore = useDefaultStore()

            if (isSelected) {
                let removePriority = 0

                this.customerParameters[rowIndex].customer_parameter_sources = this.customerParameters[rowIndex].customer_parameter_sources.filter((param) => {
                    const selected = param.display_name === value.display_name

                    if (selected) {
                        removePriority = param.priority
                    }

                    return !selected
                })
                this.customerParameters[rowIndex].customer_parameter_sources.forEach((param) => {
                    param.priority = (param.priority > removePriority) ? param.priority - 1 : param.priority
                })
            } else {
                const qnt = this.customerParameters[rowIndex].customer_parameter_sources.length

                this.customerParameters[rowIndex].customer_parameter_sources.push({
                    ...value,
                    priority: qnt + 1,
                })
            }

            const { success, payload, message } = await updateParametersDataSources({
                slug: this.trafficSourceSlug,
                formData: {
                    parameters: this.customerParameters.reduce((params, item) => {
                        if (item.customer_parameter_sources.length) {
                            params.push({
                                parameter: item.parameter,
                                sources: item.customer_parameter_sources,
                            })
                        }

                        return params
                    }, []),
                },
            })

            if (success) {
                defaultStore.setSuccessMessage({ message })
                this.setFacebookAdsCustomerParameters(payload.facebook_ads_customer_parameters)
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },
    },
})
